import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Mail, Printer } from "lucide-react";
import { api, eur, dt, errMsg, download, today } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { DateField } from "@/components/DateField";

const primo = () => `${today().slice(0, 8)}01`;
const ora = (iso) => (iso ? new Date(iso).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" }) : "");
const pagato = (v) => [v.contanti && `Contanti ${eur(v.contanti)}`, v.elettronico && `Carta ${eur(v.elettronico)}`, v.bonus && `Bonus ${eur(v.bonus)}`].filter(Boolean).join(" · ");

const email = async (v, reload) => {
  const m = window.prompt("Email del cliente", v.email_inviata || "");
  if (!m) return;
  try { await api.post(`/corrispettivi/vendite/${v.id}/email`, { email: m.trim() }); toast.success(`Scontrino inviato a ${m.trim()}`); reload(); } catch (e) { toast.error(errMsg(e)); }
};

const Totali = ({ items }) => {
  const ok = items.filter((v) => v.stato !== "annullata");
  const s = (k) => ok.reduce((a, v) => a + (v[k] || 0), 0);
  return (
    <div className="mb-3 grid gap-3 sm:grid-cols-4" data-testid="receipts-totals">
      {[["Scontrini", ok.length, false], ["Totale", s("totale"), true], ["Contanti", s("contanti"), true], ["Carta / elettronico", s("elettronico"), true]].map(([l, v, m]) => (
        <div key={l} className="rounded-lg border border-sky-200 bg-sky-50 p-3"><p className="text-xs uppercase text-slate-500">{l}</p><p className="font-mono text-lg font-semibold">{m ? eur(v) : v}</p></div>
      ))}
    </div>
  );
};

export const Scontrini = () => {
  const [dal, setDal] = useState(primo());
  const [al, setAl] = useState(today());
  const [q, setQ] = useState("");
  const [pag, setPag] = useState("tutti");
  const [items, setItems] = useState([]);
  const load = useCallback(() => api.get("/corrispettivi/vendite", { params: { dal, al } }).then((r) => setItems(r.data)).catch((e) => toast.error(errMsg(e))), [dal, al]);
  useEffect(() => { load(); }, [load]);
  const shown = items.filter((v) => (pag === "tutti" || (v[pag] || 0) > 0)).filter((v) => !q || `${v.numero} ${v.rt_numero} ${v.righe.map((r) => r.descrizione).join(" ")}`.toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="mb-3 flex flex-wrap items-end gap-3">
        <label className="text-xs font-semibold uppercase text-slate-500">Dal<DateField clearable className="mt-1 w-40" testid="receipts-from" value={dal} onChange={(v) => setDal(v)} /></label>
        <label className="text-xs font-semibold uppercase text-slate-500">Al<DateField clearable className="mt-1 w-40" testid="receipts-to" value={al} onChange={(v) => setAl(v)} /></label>
        <div className="flex rounded-lg border border-sky-200 bg-sky-50 p-1" data-testid="receipts-pay-filter">
          {[["tutti", "Tutti"], ["contanti", "Contanti"], ["elettronico", "Carta"]].map(([k, l]) => (
            <button key={k} type="button" data-testid={`receipts-pay-${k}`} onClick={() => setPag(k)} className={`rounded-md px-3 py-1.5 text-sm font-medium ${pag === k ? "bg-sky-600 text-white" : "text-sky-900 hover:bg-sky-100"}`}>{l}</button>
          ))}
        </div>
        <Input className="w-64" placeholder="Cerca numero, n. RT o articolo" data-testid="receipts-search" value={q} onChange={(e) => setQ(e.target.value)} />
      </div>
      <Totali items={shown} />
      {shown.length === 0 ? <p className="text-sm text-slate-600" data-testid="receipts-empty">Nessuno scontrino nel periodo.</p> : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow><TableHead>Numero</TableHead><TableHead>Data e ora</TableHead><TableHead>Articoli</TableHead><TableHead>Pagamento</TableHead><TableHead>N. RT</TableHead><TableHead className="text-right">Totale</TableHead><TableHead /></TableRow></TableHeader>
            <TableBody>
              {shown.map((v) => (
                <TableRow key={v.id} data-testid={`receipt-row-${v.id}`} className={v.stato === "annullata" ? "opacity-50" : ""}>
                  <TableCell className="font-mono font-semibold">{v.numero}{v.stato === "annullata" && <span className="ml-2 text-xs text-rose-600">Annullato</span>}</TableCell>
                  <TableCell className="whitespace-nowrap text-sm">{dt(v.data)} {ora(v.created_at)}</TableCell>
                  <TableCell className="max-w-xs truncate text-sm" title={v.righe.map((r) => `${r.quantita} x ${r.descrizione}`).join(", ")}>{v.righe.map((r) => `${r.quantita} x ${r.descrizione}`).join(", ")}</TableCell>
                  <TableCell className="whitespace-nowrap text-xs">{pagato(v)}</TableCell>
                  <TableCell className="font-mono text-xs">{v.rt_numero || "-"}</TableCell>
                  <TableCell className="text-right font-mono font-semibold">{eur(v.totale)}</TableCell>
                  <TableCell className="whitespace-nowrap text-right">
                    <Button variant="ghost" size="icon" title="Promemoria PDF" data-testid={`receipt-pdf-${v.id}`} onClick={() => download(`/corrispettivi/vendite/${v.id}/pdf`).catch((e) => toast.error(e.message))}><Printer className="h-4 w-4" /></Button>
                    {v.stato !== "annullata" && <Button variant="ghost" size="icon" title="Invia via email" data-testid={`receipt-email-${v.id}`} onClick={() => email(v, load)}><Mail className="h-4 w-4" /></Button>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
};
